import type { KAPLAYCtx } from "kaplay";
import type { GameSession } from "../client/ws/GameSession";
import type { GameClient } from "../client/controller/GameClient";
import type { GameStateSnapshotMessage, ActionPromptMessage } from "../client/ws/OutboundMessage";
import type { InboundMessage } from "../client/ws/InboundMessage";
import type { LocalGameState } from "../client/model/LocalGameState";
import { describeEvent } from "../client/view/EventPrinter";
import { computeLayout, renderField, canvasToField, FIELD_PADDING } from "./fieldRenderer";
import {
  createGamePanel,
  showPrompt,
  renderIdlePanel,
  appendToEventLog,
  renderHoverCard,
  clearHoverCard,
  type RenderState,
  type PromptContext,
} from "./promptPanel";

export function setupGameScene(k: KAPLAYCtx, session: GameSession, isHome: boolean, headerEl: HTMLDivElement): void {
  const state = session.state;
  let promptActive = false;

  const renderState: RenderState = {
    highlights: new Set<string>(),
    moveRange: new Set<string>(),
    tackleHighlights: new Set<string>(),
    throwHighlights: new Set<string>(),
    placementPreview: new Map<string, [number, number]>(),
    movePath: [],
    movePathOrigin: null,
    previewLine: null,
    fieldClickHandler: null,
  };

  // ─── Movement animation state ─────────────────────────────────────────────
  const animatedPositions = new Map<string, [number, number]>();
  const animationPathsMap = new Map<string, Array<[number, number]>>();
  let animationVersion = 0;

  function animateMovements(paths: Map<string, Array<[number, number]>>): void {
    if (paths.size === 0) return;
    const version = ++animationVersion;
    const maxLen = Math.max(...[...paths.values()].map((p) => p.length));

    for (const [pid, path] of paths) {
      animationPathsMap.set(pid, path);
      if (path.length > 0) animatedPositions.set(pid, path[0]);
    }

    let step = 1;
    function advance(): void {
      if (animationVersion !== version) {
        for (const [pid] of paths) { animatedPositions.delete(pid); animationPathsMap.delete(pid); }
        return;
      }
      if (step >= maxLen) {
        for (const [pid] of paths) { animatedPositions.delete(pid); animationPathsMap.delete(pid); }
        return;
      }
      for (const [pid, path] of paths) {
        if (step < path.length) animatedPositions.set(pid, path[step]);
      }
      step++;
      setTimeout(advance, 110);
    }
    setTimeout(advance, 110);
  }

  createGamePanel();

  let hoveredPlayerId: string | null = null;

  function refreshPanel(): void {
    renderIdlePanel(state);
    if (hoveredPlayerId) renderHoverCard(state, hoveredPlayerId);
  }

  refreshPanel();

  // ─── Drag-to-pan state ────────────────────────────────────────────────────
  const panX = 0; // horizontal panning disabled
  let panY = 0;
  let isDragging = false;
  let hasDragged = false;
  let dragStartY = 0;
  let panYAtDragStart = 0;

  function clampPanY(py: number): number {
    const layout0 = computeLayout(k.canvas.width, k.canvas.height, state, 0, 0);
    const fieldTotalH = layout0.cellH * state.fieldHeight;
    // max scroll up: bottom of field aligns with bottom of canvas
    const panYMin = Math.min(0, k.canvas.height - FIELD_PADDING - fieldTotalH);
    // max scroll down: top of field stays at FIELD_PADDING (no gap at top)
    return Math.max(panYMin, Math.min(0, py));
  }

  k.canvas.addEventListener("pointerdown", (e) => {
    isDragging = true;
    hasDragged = false;
    dragStartY = e.clientY;
    panYAtDragStart = panY;
    k.canvas.setPointerCapture(e.pointerId);
  });

  k.canvas.addEventListener("pointermove", (e) => {
    if (!isDragging) return;
    const dy = e.clientY - dragStartY;
    if (Math.abs(dy) > 4) hasDragged = true;
    panY = clampPanY(panYAtDragStart + dy);
  });

  k.canvas.addEventListener("pointerup", () => { isDragging = false; });
  k.canvas.addEventListener("pointercancel", () => { isDragging = false; });

  k.canvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    panY = clampPanY(panY - e.deltaY);
  }, { passive: false });

  // ─── Pan-to-LOS ───────────────────────────────────────────────────────────

  function panToShowLines(): void {
    const los = state.lineOfScrimmage;
    if (los === null) return;
    const fdl = state.firstDownLine ?? (state.offenseGoingNorth ? state.fieldHeight - 1 : 0);

    // Canvas y for a field row at panY=0: FIELD_PADDING + (fieldHeight-1-gy)*cellH
    const layout0 = computeLayout(k.canvas.width, k.canvas.height, state, 0, 0);
    const { cellH, y: base } = layout0;
    const losCY0 = base + (state.fieldHeight - 1 - los) * cellH;
    const fdlCY0 = base + (state.fieldHeight - 1 - fdl) * cellH;

    // Adding panY=p shifts every row by p on canvas, so visible range at p:
    //   top line >= margin and bottom line <= canvasH - margin
    const margin = cellH * 1.5;
    const topCY0 = Math.min(losCY0, fdlCY0);
    const botCY0 = Math.max(losCY0, fdlCY0);
    const pMin = margin - topCY0;
    const pMax = k.canvas.height - margin - botCY0;

    const targetPanY = pMin <= pMax
      ? Math.round((pMin + pMax) / 2)   // both fit — center between them
      : Math.round(k.canvas.height / 2 - (losCY0 + fdlCY0) / 2); // too tall — center midpoint

    panY = clampPanY(targetPanY);
  }

  // ─── Header helpers ───────────────────────────────────────────────────────
  const scoreEl = document.createElement("span");
  Object.assign(scoreEl.style, { fontSize: "16px", fontWeight: "bold", color: "#ffd700", letterSpacing: "1px" });
  const infoEl = document.createElement("span");
  Object.assign(infoEl.style, { fontSize: "11px", color: "#888", letterSpacing: "0.5px" });
  headerEl.appendChild(scoreEl);
  headerEl.appendChild(infoEl);

  function updateHeader(): void {
    scoreEl.textContent = `HOME ${state.homeScore}   —   ${state.awayScore} AWAY`;
    const maxClock = state.downMax > 0 ? state.downMax * 5 : 15;
    infoEl.textContent = `Down ${state.downNumber}/${state.downMax}  ·  Clock ${state.gameClock}/${maxClock}  ·  Bomb ${state.bombTracker}  ·  ${state.phase}`;
  }

  // ─── Drawing ──────────────────────────────────────────────────────────────
  k.onDraw(() => {
    updateHeader();

    const cw = k.canvas.width;
    const ch = k.canvas.height;
    const layout = computeLayout(cw, ch, state, panX, panY);
    renderField(k, state, layout, {
      highlights: renderState.highlights,
      moveHighlights: renderState.moveRange,
      tackleHighlights: renderState.tackleHighlights,
      throwHighlights: renderState.throwHighlights,
      placementPreview: renderState.placementPreview,
      movePath: renderState.movePath,
      movePathOrigin: renderState.movePathOrigin,
      previewLine: renderState.previewLine,
      animatedPositions,
      animationPaths: animationPathsMap,
      myTeamIsHome: isHome,
    });
  });

  // ─── Hover routing ────────────────────────────────────────────────────────
  k.onMouseMove(() => {
    if (isDragging) return;
    const mp = k.mousePos();
    const layout = computeLayout(k.canvas.width, k.canvas.height, state, panX, panY);
    const cell = canvasToField(mp.x, mp.y, layout, state);

    let found: string | null = null;
    if (cell) {
      for (const [pid, pos] of state.playerPositions) {
        if (pos[0] === cell.x && pos[1] === cell.y) { found = pid; break; }
      }
    }

    if (found !== hoveredPlayerId) {
      hoveredPlayerId = found;
      if (found) renderHoverCard(state, found);
      else clearHoverCard();
    }
  });

  // ─── Click routing ────────────────────────────────────────────────────────
  k.onClick(() => {
    if (hasDragged) { hasDragged = false; return; }
    if (!renderState.fieldClickHandler) return;
    const mp = k.mousePos();
    const layout = computeLayout(k.canvas.width, k.canvas.height, state, panX, panY);
    const cell = canvasToField(mp.x, mp.y, layout, state);
    if (cell) renderState.fieldClickHandler(cell.x, cell.y);
  });

  // ─── GameClient ───────────────────────────────────────────────────────────
  const client: GameClient = {
    onSnapshot(_snap: GameStateSnapshotMessage, _state: LocalGameState): void {
      if (!promptActive) refreshPanel();
    },

    onEventBatch(events: Record<string, unknown>[], _state: LocalGameState): void {
      const LOS_EVENTS = new Set(["DriveStarted", "BallPlaced", "FirstDownAchieved", "FormationsRevealed"]);
      let losChanged = false;
      for (const event of events) {
        if (event["type"] === "FormationsRevealed") {
          renderState.placementPreview.clear();
        }
        if (LOS_EVENTS.has(event["type"] as string)) losChanged = true;
        const desc = describeEvent(event, state) ?? `[${event["type"]}]`;
        appendToEventLog([desc]);
      }
      if (state.pendingMovementPaths.size > 0) {
        animateMovements(new Map(state.pendingMovementPaths));
      }
      if (losChanged) panToShowLines();
      if (!promptActive) refreshPanel();
    },

    async onActionPrompt(prompt: ActionPromptMessage, _state: LocalGameState): Promise<InboundMessage | null> {
      promptActive = true;
      hoveredPlayerId = null;
      clearHoverCard();
      const ctx: PromptContext = {
        state,
        isHome,
        renderState,
        getLayout: () => computeLayout(k.canvas.width, k.canvas.height, state, panX, panY),
      };
      const response = await showPrompt(prompt, ctx);
      promptActive = false;
      refreshPanel();
      return response;
    },

    onFormationAccepted(): void {
      appendToEventLog(["✓ Formation accepted."]);
      refreshPanel();
    },

    onError(error: string): void {
      appendToEventLog([`[ERROR] ${error}`]);
      refreshPanel();
    },
  };

  session.setGameClient(client);

  // Suppress default touch scroll on the canvas
  k.canvas.style.touchAction = "none";

}
